import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { faLock, faLockOpen, faCalendarAlt, faEye, faUser, faVideo, faMagnifyingGlassPlay } from '@/fontawesome';
import { formatDate, shortObjectID } from '@/utils/helpers';
import type { ChatSession } from '@/models/chat-session';
import { isVideoChat } from '@/models/chat-session';

interface SessionsTableProps {
  sessions: ChatSession[];
  showUserColumn?: boolean;
  children?: React.ReactNode;
}

export default function SessionsTable({ sessions, showUserColumn = false, children }: SessionsTableProps) {
  const truncateTitle = (title: string, maxLength: number = 50) => {
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength) + '...';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Titel
              </th>
              {showUserColumn && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Benutzer
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Erstellt
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Aktionen
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {sessions.map((session) => (
              <tr key={session.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <span className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                      {shortObjectID(session.id)}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {isVideoChat(session) && (
                      <FontAwesomeIcon
                        icon={faVideo}
                        className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2"
                        title="Video-Chat Session"
                      />
                    )}
                    {session.type === 'VIDEO_SEARCH' && (
                      <FontAwesomeIcon
                        icon={faMagnifyingGlassPlay}
                        className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2"
                        title="Video-Finder Session"
                      />
                    )}
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {truncateTitle(session.title)}
                    </div>
                  </div>
                </td>
                {showUserColumn && (
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faUser} className="w-4 h-4 text-gray-400 mr-2" />
                      <Link
                        to={`/admin/users/${session.userID}`}
                        className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-mono cursor-pointer"
                        title="Benutzer ansehen"
                      >
                        {shortObjectID(session.userID)}
                      </Link>
                    </div>
                  </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <FontAwesomeIcon 
                      icon={session.closed ? faLock : faLockOpen} 
                      className={`w-4 h-4 mr-2 ${
                        session.closed 
                          ? 'text-red-500 dark:text-red-400' 
                          : 'text-green-500 dark:text-green-400'
                      }`} 
                    />
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      session.closed 
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400'
                        : 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400'
                    }`}>
                      {session.closed ? 'Geschlossen' : 'Aktiv'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-2" />
                    {formatDate(session.created)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Link
                    to={`/chats/${session.id}`}
                    className="inline-flex items-center justify-center w-8 h-8 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors cursor-pointer"
                    title="Session ansehen"
                  >
                    <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {children}
    </div>
  );
}